import app from 'flarum/forum/app';

/*
 * 标签数据源（着陆页四卡片 + 底部班级导航共用）。
 *
 * ============================================================
 * 为什么不用 app.store.all('tags') / app.store.find('tags')
 * ============================================================
 * 实测在首页（尤其后台把首页设成 Tags 页时），app.store.find('tags')
 * 并不保证把标签推进 store，或者只推入部分标签（只有主标签没有次标签）。
 * 结果是 store 里 tags 为空 / 不全 → 班级和次标签全部匹配失败 →
 * 解析出的地址一律退化成 '/'（表现：卡片 href="/"、点下去没反应）。
 *
 * 这里改为**自己请求 API 拿原始 JSON**，解析成纯对象数组缓存起来：
 *   - 不依赖 Flarum model 的 isPrimary()/canStartDiscussion()/parent() 是否存在
 *   - 字段直接从 attributes / relationships 里读，版本差异不会导致抛异常
 *   - 拿到的是全量标签（/api/tags 默认返回全部）
 *
 * 数据结构（每条）：
 *   { id, name, slug, parentId, position, canStartDiscussion }
 */

let cache = null; // 解析结果数组（null = 还没拿到）
let pending = null; // 进行中的 Promise

// 把 JSON:API 载荷解析成纯对象数组
function normalizeList(payload) {
  const data = (payload && payload.data) || [];
  if (!data.length) return [];

  return data.map((d) => {
    const a = d.attributes || {};
    const rel = (d.relationships || {}).parent;

    let parentId = null;
    if (rel && rel.data && rel.data.id !== undefined && rel.data.id !== null) {
      parentId = String(rel.data.id);
    } else if (a.parentId !== undefined && a.parentId !== null) {
      parentId = String(a.parentId);
    }

    return {
      id: String(d.id),
      name: a.name || '',
      slug: a.slug || '',
      parentId: parentId,
      position: a.position === undefined || a.position === null ? null : Number(a.position),
      canStartDiscussion: a.canStartDiscussion === true,
    };
  });
}

function apiUrl() {
  let url = '/api/tags';

  try {
    const base = app.forum && app.forum.attribute ? app.forum.attribute('apiUrl') : null;
    if (base) url = String(base).replace(/\/+$/, '') + '/tags';
  } catch (e) {
    // 拿不到就用相对路径
  }

  return url;
}

// 走 Flarum 封装（自动带正确的 Accept / CSRF / base path）
function requestByApp(url) {
  return new Promise((resolve, reject) => {
    try {
      app.request({ method: 'GET', url: url }).then(resolve, reject);
    } catch (e) {
      reject(e);
    }
  });
}

// 兜底：原生 fetch（app.request 不可用或失败时）
function requestByFetch(url) {
  return new Promise((resolve, reject) => {
    try {
      fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/vnd.api+json' },
      })
        .then((r) => r.json())
        .then(resolve, reject);
    } catch (e) {
      reject(e);
    }
  });
}

/*
 * 拉取标签（带缓存，重复调用只会真正请求一次）。
 *
 * 刻意 defer 到下一个宏任务：调用方可能在 app.initializers.add() 里同步执行，
 * 那时 app.request / app.forum 还没就绪。
 */
export function loadTags() {
  if (cache) return Promise.resolve(cache);
  if (pending) return pending;

  pending = new Promise((resolve) => {
    const done = (payload) => {
      const list = normalizeList(payload);
      if (list.length) cache = list;
      resolve(cache || []);
    };

    const fail = () => {
      requestByFetch(apiUrl()).then(done, () => resolve([]));
    };

    setTimeout(() => {
      requestByApp(apiUrl()).then(done, fail);
    }, 0);
  });

  return pending;
}

// 已解析的标签数组（未加载完成时为 []）
export function getTags() {
  return cache || [];
}

// 是否已经拿到数据
export function tagsLoaded() {
  return !!cache;
}
